import { observer } from 'mobx-react'
import { useContext, useEffect, useRef, useState } from 'react'
import { useInterval } from 'usehooks-ts'
import type { EasySignGetContextResponse } from '@components/SignDocumentCommon/types'
import { API_URL, EASYSIGN_SERVICE_PATH } from '@constants/apiUrl'
import { axiosInstance } from '@methods/axiosConfig'
import { devLog } from '@methods/devLog'
import { forceRedirect } from '@methods/forceRedirect'
import { StoreContext } from '@store/Root.store'
import { getErrorRedirectUrl, postSendOtp } from './methods'
import { Init } from './steps/Init'
import { SetPhoneNumber } from './steps/SetPhoneNumber'
import { VerifyOtpCode } from './steps/VerifyOtpCode'

export const EasySignSmsOtpVerificationPage = observer(() => {
  const store = useContext(StoreContext)

  const [otpContext, setOtpContext] = useState<EasySignGetContextResponse['smsOtp']>()
  const [resendCooldown, _setResendCooldown] = useState(0)
  const resendCooldownRef = useRef(resendCooldown)
  const setResendCooldown = (value: number) => {
    resendCooldownRef.current = value
    _setResendCooldown(value)
  }
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null)
  const [currentStep, setCurrentStep] = useState<'init' | 'verifyOtp' | 'changePhoneNumber'>('init')

  const getContext = async () => {
    setCurrentStep('init')
    try {
      const { data } = await axiosInstance.get<EasySignGetContextResponse>(
        `${API_URL}/${EASYSIGN_SERVICE_PATH}/get-context`,
        {
          withCredentials: true,
        }
      )

      setOtpContext(data.smsOtp)

      const sesContextDataOtp = data.smsOtp

      if (sesContextDataOtp?.isEnabled && !sesContextDataOtp?.isVerified) {
        decideNextStep(sesContextDataOtp)
      } else {
        store.ScenarioState.nextPage()
      }
    } catch (error) {
      devLog('Error fetching EasySign context:', error)
    }
  }

  const decideNextStep = async (otpContext: EasySignGetContextResponse['smsOtp']) => {
    if (otpContext.phoneNumber) {
      try {
        if (resendCooldownRef.current === 0) {
          await postSendOtp(otpContext?.phoneNumber)
          setResendCooldown(otpContext?.resendCooldownSeconds || 30)
        }
        setCurrentStep('verifyOtp')
      } catch (e) {
        devLog('Error sending OTP in decideNextStep:', e)
        onError()
      }
    } else if (otpContext.isPhoneNumberEditable) {
      setCurrentStep('changePhoneNumber')
    } else {
      onError()
    }
  }

  const onError = () => {
    setCurrentStep('init')

    forceRedirect(getErrorRedirectUrl())
  }

  useInterval(
    () => {
      if (resendCooldown > 0) {
        setResendCooldown(resendCooldown - 1)
      }
    },
    resendCooldown > 0 ? 1000 : null
  )

  useEffect(() => {
    void getContext()
  }, [])

  if (currentStep === 'init' || !otpContext) {
    return <Init />
  }

  if (currentStep === 'verifyOtp' && otpContext) {
    return (
      <VerifyOtpCode
        otpContext={otpContext}
        onError={onError}
        resendCooldown={resendCooldown}
        setResendCooldown={setResendCooldown}
        attemptsRemaining={attemptsRemaining}
        setAttemptsRemaining={setAttemptsRemaining}
        navigateToChangePhoneNumber={() => setCurrentStep('changePhoneNumber')}
      />
    )
  }

  if (currentStep === 'changePhoneNumber' && otpContext) {
    return (
      <SetPhoneNumber
        otpContext={otpContext}
        resendCooldown={resendCooldown}
        setResendCooldown={setResendCooldown}
        navigateToVerifyOtp={async () => {
          await getContext()
        }}
      />
    )
  }
})
